"use client";

import { useEffect } from "react";
import * as Sentry from "@sentry/nextjs";

import { ensurePostHog } from "@/lib/analytics/posthog";

/**
 * Defers PostHog bootstrap until first user interaction OR a 5 s timeout
 * (whichever comes first). Same pattern as `ClarityLoader.tsx` — third-party
 * analytics scripts are notorious for being captured by Lighthouse's no-
 * interaction trace, so we delay init until the page is actually engaged.
 *
 * Why 5 s here vs 10 s for Clarity: Clarity recordings need a full Lighthouse
 * pass clear, so its window is wider. PostHog only sends a single tiny init
 * payload + page-view, so a shorter fallback gives the foundation event the
 * best chance to land before the user navigates away on a quick visit.
 *
 * Identify follow-up (Phase 0 F0-Client):
 * After init lands, we fetch `/api/auth/me` once and call `posthog.identify()`
 * if a session is present. Server-side identify in `verify/route.ts` (F0-Server)
 * covers the magic-link landing case; this catches every other entry path
 * (direct visit to /dashboard after a return, fresh tab on a logged-in cookie).
 * Anonymous visits → /api/auth/me returns 401 → no identify, no error.
 *
 * If `NEXT_PUBLIC_POSTHOG_KEY` is unset, `ensurePostHog()` short-circuits and
 * this component is effectively a no-op — safe to mount in every environment.
 */

const FALLBACK_TIMEOUT_MS = 5_000;

interface AuthMeResponse {
  user: {
    id: string;
    email: string;
  } | null;
}

async function identifyFromAuthMe(): Promise<void> {
  try {
    const res = await fetch("/api/auth/me", { credentials: "same-origin" });
    if (!res.ok) return; // 401 anon visit — expected, not an error
    const data = (await res.json()) as AuthMeResponse;
    if (!data.user) return;
    const client = await ensurePostHog();
    if (!client) return;
    const emailDomain = data.user.email.split("@")[1] ?? "unknown";
    client.identify(data.user.id, { email_domain: emailDomain });
  } catch (err) {
    // Identify failure must never break the auth flow — PostHog is best-effort.
    // Surface to Sentry so we can spot regressions (e.g. /api/auth/me 500s).
    Sentry.captureException(err, {
      tags: { area: "analytics.client.identify" },
    });
  }
}

export function PostHogLoader() {
  useEffect(() => {
    let loaded = false;

    const bootstrap = () => {
      if (loaded) return;
      loaded = true;
      void ensurePostHog().then(() => {
        void identifyFromAuthMe();
      });
    };

    const events: (keyof WindowEventMap)[] = [
      "mousedown",
      "touchstart",
      "scroll",
      "keydown",
    ];

    const onInteract = () => {
      bootstrap();
      cleanup();
    };

    const cleanup = () => {
      clearTimeout(timer);
      events.forEach((e) => window.removeEventListener(e, onInteract));
    };

    events.forEach((e) =>
      window.addEventListener(e, onInteract, { passive: true, once: true }),
    );
    const timer = setTimeout(() => {
      bootstrap();
      cleanup();
    }, FALLBACK_TIMEOUT_MS);

    return cleanup;
  }, []);

  return null;
}
